import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { trainingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

const TrainingPage = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  
  const queryClient = useQueryClient();

  // 获取培训列表
  const { data, isLoading, error } = useQuery(
    ['trainings', { status: statusFilter, search, page }],
    () => trainingAPI.getAll({
      status: statusFilter,
      search,
      page,
      per_page: 10
    })
  );

  // 创建培训
  const createMutation = useMutation(
    (data) => trainingAPI.create(data),
    {
      onSuccess: () => {
        toast.success('培训计划创建成功');
        setShowCreateModal(false);
        queryClient.invalidateQueries('trainings');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '创建失败');
      }
    }
  );

  // 更新培训
  const updateMutation = useMutation(
    ({ id, data }) => trainingAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('培训计划更新成功');
        setShowEditModal(false);
        setSelectedTraining(null);
        queryClient.invalidateQueries('trainings');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  // 删除培训
  const deleteMutation = useMutation(
    (id) => trainingAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('培训计划删除成功');
        queryClient.invalidateQueries('trainings');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  const handleCreate = (data) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const handleDelete = (id) => {
    if (window.confirm('确定要删除这个培训计划吗？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (training) => {
    setSelectedTraining(training);
    setShowEditModal(true);
  };

  const trainings = data?.trainings || [];
  const pagination = data ? {
    total: data.total,
    page: data.page,
    per_page: data.per_page,
    pages: data.pages
  } : null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待开始', color: 'badge-primary' },
      ongoing: { label: '进行中', color: 'badge-warning' },
      completed: { label: '已完成', color: 'badge-success' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'badge-gray' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未设置';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '无效日期';
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题和操作按钮 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">安全培训管理</h1>
          <p className="text-gray-600 mt-1">管理安全培训计划和记录</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 btn-primary"
        >
          <i className="fa fa-plus mr-2"></i>
          新建培训计划
        </button>
      </div>

      {/* 搜索和过滤 */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索培训..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fa fa-search text-gray-400"></i>
              </div>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">所有状态</option>
              <option value="pending">待开始</option>
              <option value="ongoing">进行中</option>
              <option value="completed">已完成</option>
            </select>
          </div>
        </div>
      </div>

      {/* 培训列表 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner mx-auto"></div>
            <p className="mt-4 text-gray-600">加载培训计划中...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="fa fa-exclamation-circle text-red-500 mr-3"></i>
            <div>
              <p className="text-red-800 font-medium">加载失败</p>
              <p className="text-red-600 text-sm mt-1">无法加载培训计划列表，请稍后重试</p>
            </div>
          </div>
        </div>
      ) : trainings.length === 0 ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-12 text-center">
            <i className="fa fa-graduation-cap text-gray-300 text-4xl mb-3"></i>
            <p className="text-gray-500">暂无培训计划</p>
            <p className="text-gray-400 text-sm mt-1">点击"新建培训计划"按钮开始创建</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      培训名称
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      培训类型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      培训时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      参训人数
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trainings.map((training) => (
                    <tr key={training.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{training.title}</div>
                        <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                          {training.description || '无描述'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {training.type === 'theory' && '理论培训'}
                          {training.type === 'practice' && '实操培训'}
                          {training.type === 'mixed' && '混合培训'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(training.start_time)}
                        </div>
                        <div className="text-xs text-gray-500">
                          至 {formatDate(training.end_time)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {training.participants_count} / {training.max_participants || '不限'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(training.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(training)}
                          className="text-primary hover:text-blue-700 mr-3"
                        >
                          <i className="fa fa-edit mr-1"></i>编辑
                        </button>
                        <button
                          onClick={() => handleDelete(training.id)}
                          className="text-danger hover:text-red-700"
                        >
                          <i className="fa fa-trash mr-1"></i>删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 分页 */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    显示 <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> 到{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.per_page, pagination.total)}
                    </span>{' '}
                    条，共 <span className="font-medium">{pagination.total}</span> 条记录
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <i className="fa fa-chevron-left"></i>
                    </button>
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagination.page === pageNum
                              ? 'z-10 bg-primary border-primary text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, pagination.page + 1))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <i className="fa fa-chevron-right"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 创建培训模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => setShowCreateModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">新建培训计划</h3>
              </div>
              <div className="px-6 py-4">
                <TrainingForm
                  onSubmit={handleCreate}
                  onCancel={() => setShowCreateModal(false)}
                  isLoading={createMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑培训模态框 */}
      {showEditModal && selectedTraining && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTraining(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">编辑培训计划</h3>
              </div>
              <div className="px-6 py-4">
                <TrainingForm
                  training={selectedTraining}
                  onSubmit={(data) => handleUpdate(selectedTraining.id, data)}
                  onCancel={() => {
                    setShowEditModal(false);
                    setSelectedTraining(null);
                  }}
                  isLoading={updateMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 培训表单组件
const TrainingForm = ({ training, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: training ? {
      title: training.title,
      description: training.description || '',
      type: training.type || 'theory',
      start_time: training.start_time ? new Date(training.start_time).toISOString().slice(0, 16) : '',
      end_time: training.end_time ? new Date(training.end_time).toISOString().slice(0, 16) : '',
      location: training.location || '',
      trainer: training.trainer || '',
      participants_count: training.participants_count || 0,
      max_participants: training.max_participants || '',
      status: training.status || 'pending',
    } : {}
  });
  const [coverPhoto, setCoverPhoto] = useState(training?.cover_photo || null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = (data) => {
    // 转换数据类型
    const formData = {
      ...data,
      participants_count: parseInt(data.participants_count) || 0,
      max_participants: data.max_participants ? parseInt(data.max_participants) : null,
      cover_photo: coverPhoto,
    };
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {/* 培训封面照片 */}
        <div>
          <label className="form-label">培训封面</label>
          <div className="flex items-center space-x-4">
            <div className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
              {coverPhoto ? (
                <img src={coverPhoto} alt="培训封面" className="w-full h-full object-cover" />
              ) : (
                <i className="fa fa-image text-gray-400 text-2xl"></i>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                id="training-cover"
              />
              <label
                htmlFor="training-cover"
                className="btn-secondary cursor-pointer inline-block"
              >
                <i className="fa fa-upload mr-2"></i>
                {coverPhoto ? '更换封面' : '上传封面'}
              </label>
              {coverPhoto && (
                <button
                  type="button"
                  onClick={() => setCoverPhoto(null)}
                  className="ml-2 text-danger hover:text-red-700 text-sm"
                >
                  <i className="fa fa-trash mr-1"></i>删除
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="form-label">培训名称 *</label>
          <input
            type="text"
            {...register('title', { required: '请输入培训名称' })}
            className="form-input"
            placeholder="请输入培训名称"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-danger">{errors.title.message}</p>
          )}
        </div>

        <div>
          <label className="form-label">培训描述</label>
          <textarea
            {...register('description')}
            rows="2"
            className="form-input"
            placeholder="请输入培训描述"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">培训类型</label>
            <select {...register('type')} className="form-input">
              <option value="theory">理论培训</option>
              <option value="practice">实操培训</option>
              <option value="mixed">混合培训</option>
            </select>
          </div>

          <div>
            <label className="form-label">状态</label>
            <select {...register('status')} className="form-input">
              <option value="pending">待开始</option>
              <option value="ongoing">进行中</option>
              <option value="completed">已完成</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">开始时间</label>
            <input
              type="datetime-local"
              {...register('start_time')}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">结束时间</label>
            <input
              type="datetime-local"
              {...register('end_time')}
              className="form-input"
            />
          </div>
        </div>

        <div>
          <label className="form-label">培训地点</label>
          <input
            type="text"
            {...register('location')}
            className="form-input"
            placeholder="请输入培训地点"
          />
        </div>

        <div>
          <label className="form-label">培训讲师</label>
          <input
            type="text"
            {...register('trainer')}
            className="form-input"
            placeholder="请输入培训讲师姓名"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">当前参训人数</label>
            <input
              type="number"
              {...register('participants_count')}
              className="form-input"
              min="0"
            />
          </div>

          <div>
            <label className="form-label">最大参训人数</label>
            <input
              type="number"
              {...register('max_participants')}
              className="form-input"
              min="1"
              placeholder="不填表示不限"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              {training ? '保存中...' : '创建中...'}
            </>
          ) : (
            training ? '保存更改' : '创建计划'
          )}
        </button>
      </div>
    </form>
  );
};

export default TrainingPage;